import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

export const numberWithCommas = (value, allowNegative) => {
    if (_.isUndefined(value)) return;
    if (allowNegative) {
        const numbersWithNegative = value.toString().replace(/[^\d.-]/g, '');
        if (numbersWithNegative.match(/^-|\d/g)) {
            return numbersWithNegative
                .match(/^-|\d/g)
                .join('')
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return numbersWithNegative;
    }
    return value
        .toString()
        .replace(/\D/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const numberWithoutCommas = (value) => {
    if (_.isUndefined(value)) return;
    const number = value.replace(/(!?-)(\D)/g, '').replace(/,/g, '');
    return parseFloat(number);
};

export const germanPostcodeFormatter = (postcode) => {
    return numbersOnly(postcode).slice(0, 5);
};

export const dateFormatter = (date) => {
    const emptyString = '';
    const hasAllDateValues =
        _.values(date).filter(Boolean).length === _.keys(date).length;

    if (!hasAllDateValues || !_.isObject(date)) {
        return emptyString;
    }
    // converts 1 to 01
    if (date.dd && date.dd.length === 1) {
        date.dd = '0' + date.dd;
    }
    return date.dd
        ? `${date.yy}-${date.mm}-${date.dd}`
        : `${date.yy}-${date.mm}`;
};

export const dateParser = (date) => {
    if (_.isEmpty(date) || !_.isString(date)) {
        return date;
    }
    return _.omitBy(
        _.zipObject(['yy', 'mm', 'dd'], date.split('-')),
        _.isUndefined
    );
};

export const phoneFormatter = (value) => {
    if (_.isUndefined(value)) return;
    return value.replace(/[^(0-9)+()\s]/g, '');
};

export const numbersOnly = (value) => {
    if (_.isUndefined(value)) return;
    return value.replace(/[^0-9]+/g, '');
};

export const numbersOnlyParser = (value) => {
    if (_.isUndefined(value) || _.isEmpty(value)) return '';
    value = value.replace(/[^0-9]+/g, '');
    return parseFloat(value);
};

export const intlAmountFormatterDecorator = (fn) => {
    return function (value) {
        if (!value) {
            return '';
        }
        value = numbersOnly(value.toString());
        return fn.call(null, parseFloat(value));
    };
};

export const nonAlphaNumeric = (value) => {
    if (_.isUndefined(value)) return;
    return value.replace(/[\W_]+/g, '');
};

export const nonAlphaSpaces = (value) => {
    if (_.isUndefined(value)) return;
    return value.replace(/[^a-zA-Z0-9\s]+/g, '');
};

export const delay = (ms) => {
    return new Promise(function (resolve) {
        const timerId = setTimeout(() => {
            resolve();
            clearTimeout(timerId);
        }, ms); // (A)
    });
};

export const buildPaths = (obj) => {
    let paths = [],
        keys = _.keys(obj);

    _.each(keys, (key) => {
        if (_.isPlainObject(obj[key])) {
            let subPath = buildPaths(obj[key]);
            subPath = _.map(subPath, (p) => `${key}.${p}`);
            paths = paths.concat(subPath);
        } else {
            paths.push(key);
        }
    });

    return paths;
};

export const removeEmptyStrings = (obj) => {
    Object.keys(obj).forEach((key) => {
        if (obj[key] && typeof obj[key] === 'object')
            removeEmptyStrings(obj[key]);
        else if (_.isString(obj[key]) && _.isEmpty(obj[key])) delete obj[key];
    });
    return obj;
};

// TODO: Remove in favour of Cookie js package
export const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        let cookies = document.cookie.split(';');
        for (var i = 0; i < cookies.length; i++) {
            let cookie = _.trim(cookies[i]);
            if (cookie.substring(0, name.length + 1) === name + '=') {
                cookieValue = decodeURIComponent(
                    cookie.substring(name.length + 1)
                );
                break;
            }
        }
    }
    return cookieValue;
};

export const getOrSetAnonymousSplitTestUID = () => {
    let AnonymousSplitTestUID = getCookie('anonymous_split_test_uid');

    if (!AnonymousSplitTestUID) {
        AnonymousSplitTestUID = uuidv4();
        document.cookie = `anonymous_split_test_uid=${encodeURIComponent(
            AnonymousSplitTestUID
        )}; path=/`;
    }

    return AnonymousSplitTestUID;
};

export const getAge = (date, options = { timeUnit: 'years' }) => {
    if (!date) {
        return 0;
    }
    var dateToCompare =
        date.split('-').length === 2 ? new Date(`${date}-01`) : new Date(date);
    var currentDate = new Date();
    var diff = currentDate - dateToCompare;
    //Divide by 1000*60*60*24*365.25
    let timeUnitTimestamp = 31557600000;
    if (options.timeUnit === 'months') {
        // (1000*60*60*24*365.25) / 12
        timeUnitTimestamp = 2629800000;
    }
    return Math.floor(diff / timeUnitTimestamp);
};

export const serializeQuery = ({ filters, fields }) => {
    return _.map(
        filters,
        (obj) => `filter_key=${obj.key}&filter_value=${obj.value}`
    )
        .concat(_.map(fields, (item) => `field=${item}`))
        .join('&');
};

export const makePromiseCancelable = (promise) => {
    let _hasCanceled = false;
    const wrappedPromise = new Promise((res, rej) => {
        promise.then((val) => {
            _hasCanceled ? rej({ isCanceled: true }) : res(val);
        });

        promise.catch((err) => {
            _hasCanceled ? rej({ isCanceled: true }) : rej(err);
        });
    });
    return {
        promise: wrappedPromise,
        cancel: () => {
            _hasCanceled = true;
        }
    };
};

export const getPathFromUrl = (url) => {
    // Regex from https://tools.ietf.org/html/rfc3986#appendix-B changed to add the slashes to the scheme
    const urlParserRegex =
        /^(([^:\/?#]+):\/\/)?([^\/?#]*)?([^?#]*)(\?([^#]*))?(#(.*))?/; /*eslint no-useless-escape: 0*/
    const urlSegments = urlParserRegex.exec(url);

    const path = urlSegments[4];
    if (!path) {
        return '';
    }

    const queryAndFragment = urlSegments[7];
    const allPotentialPathSegments = [path, queryAndFragment];
    let urlPath = allPotentialPathSegments.filter(
        (element) => element !== undefined
    );

    return `${urlPath.join('')}`;
};

export const onlyLettersAndSpacesAndDashes = (value = '') => {
    return value.replace(/[^a-zA-Z\s\-'`]+/g, '').trim();
};

export const isAllUpperOrLowerCase = (string) => {
    if (!_.isString(string)) {
        return '';
    }
    const allUpperOrAllLowerCase = /^[A-Z\s]+$|^[a-z\s]+$/;
    return string.match(allUpperOrAllLowerCase);
};

export const capitaliseFirstLetterOfEachWord = (string) => {
    const words = string.trim().split(' ');

    const capitalisedWords = words.map((word) => {
        const wordIsEmptySpace = word === ' ';
        if (wordIsEmptySpace) {
            return ' ';
        }
        const lowerCasedWord = word.toLowerCase();
        return _.upperFirst(lowerCasedWord);
    });
    return capitalisedWords.join(' ');
};

export const fixUserCapitalisationErrors = (string) => {
    return isAllUpperOrLowerCase(string)
        ? capitaliseFirstLetterOfEachWord(string)
        : _.upperFirst(string);
};

export const getSchemaSpecificCompanyType = (companyType) => {
    const soleTraderCompanyTypes = ['other', 'op', 'st'];
    const ST = 'st';
    const LTD = 'ltd';
    return soleTraderCompanyTypes.indexOf(companyType) > -1 ? ST : LTD;
};
/* eslint-disable @typescript-eslint/no-unused-vars */
export const domOnlyProps = ({
    initialValue,
    autofill,
    onUpdate,
    valid,
    invalid,
    dirty,
    pristine,
    active,
    touched,
    visited,
    autofilled,
    error,
    formatter,
    parser,
    handleChange,
    ...domProps
}) => domProps;
/* eslint-enable @typescript-eslint/no-unused-vars */

export const testUserAgentWithRegex = (regex) => {
    return regex.test(navigator.userAgent);
};

export const mobileIsAnAppleDevice = () => {
    return testUserAgentWithRegex(/iPhone|iPad|iPod/i);
};

export const addPointerCursorToBody = () => {
    // There is a bug in the React-Bootstrap code where tooltips don't close on iOS mobile devices.
    // This is due to iOS devices not sending 'click' events unless the CSS 'cursor' property is set to 'pointer'.
    document.body.classList.add('make-tooltip-close-ios-mobile');
};

export function isLimitedCompany(application) {
    return (
        application.company.type === 'ltd' || application.company.type === 'llp'
    );
}

export function getQueryParams(locationSearch) {
    const search = locationSearch ? locationSearch : window.location.search;
    const urlSearchParams = new URLSearchParams(search);
    return Object.fromEntries(urlSearchParams.entries());
}
